/**
 * AdminLogin — Dedicated admin login page.
 *
 * Uses the Zustand adminAuthStore for authentication. On success, stores
 * token/admin flags and redirects to the admin dashboard.
 */
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import authSession from "../lib/authSession";
import { goAfterLogin } from "../lib/roles";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import Logo from "../assets/fillopsLogo.png";
import { useAdminAuth } from "../store/admin";
import { toDisplayString } from "../utils/toDisplayString";

const loginSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(2, "Password must be at least 6 characters"),
});

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login, loginLoading, loginError } = useAdminAuth();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (formData) => {
    const result = await login(formData.email, formData.password);
    if (result.success) {
      authSession.persistIdentity("admin", { email: formData.email, user: result.user });
      toast({ title: "Login successful! 👋", description: "Welcome back, Admin." });
      // Shared, role-aware redirect (the same map the route guards use).
      goAfterLogin(navigate, { role: "admin" });
    } else {
      toast({
        title: "Login failed",
        description: toDisplayString(result.error, "Invalid credentials"),
        variant: "destructive",
      });
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-white/80 dark:bg-gray-800/60 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-white/20 dark:border-gray-700"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={Logo} alt="FillopTech Logo" className="h-16 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Admin Portal
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Sign in to manage your platform
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                placeholder="admin@example.com"
                {...register("email")}
                className={`w-full pl-10 pr-3 py-2.5 border rounded-lg text-gray-700 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent focus:outline-none focus:ring-2 focus:ring-indigo-400 transition ${
                  errors.email ? "border-red-400" : "border-gray-300 dark:border-gray-700"
                }`}
              />
            </div>
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                {...register("password")}
                className={`w-full pl-10 pr-10 py-2.5 border rounded-lg text-gray-700 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent focus:outline-none focus:ring-2 focus:ring-indigo-400 transition ${
                  errors.password ? "border-red-400" : "border-gray-300 dark:border-gray-700"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>}
          </div>

          {/* Error from store */}
          {loginError && <p className="text-red-500 text-sm text-center">{loginError}</p>}

          {/* Submit */}
          <button
            type="submit"
            disabled={loginLoading}
            className={`w-full flex items-center justify-center gap-2 ${
              loginLoading ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
            } text-white font-semibold py-2.5 rounded-lg shadow-md transition duration-200`}
          >
            {loginLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Signing in...
              </>
            ) : (
              "Sign In as Admin"
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-6">
          © {new Date().getFullYear()} FillopTech Admin. All rights reserved.
        </p>
      </motion.div>
    </section>
  );
};

export default AdminLogin;
